import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Radio, Input, Select, Row, Col, Spin, message, Space, Divider } from 'antd';
import { ExperimentOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { useAuth } from '../context/AuthContext';
import NutritionOrderForm from './NutritionOrderForm';
import LifestyleOrderCardsModal from './LifestyleOrderCardsModal';
import PredefinedLabOrdersModal from './PredefinedLabOrdersModal';
import MomentDatePicker from './MomentDatePicker';

const { Option } = Select;
const { TextArea } = Input;

const AddOrderModal = ({ visible, onClose, onOrderCreated, patientId, title = "Add New Order", openMedicationModalForOrders, preselectedOrderType }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedOrderType, setSelectedOrderType] = useState(null);
  const { user } = useAuth();
  const [isLifestyleOrderCardsModalVisible, setIsLifestyleOrderCardsModalVisible] = useState(false);
  const [isPredefinedLabOrdersModalVisible, setIsPredefinedLabOrdersModalVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (preselectedOrderType) {
        form.setFieldsValue({ orderType: preselectedOrderType });
        setSelectedOrderType(preselectedOrderType);
      } else {
        setSelectedOrderType(null);
      }
    }
  }, [visible, preselectedOrderType, form]);

  const handleOrderTypeChange = (value) => {
    setSelectedOrderType(value);
    form.setFieldsValue({ lifestyleCategory: undefined });
    // Note: Removed auto-closing behavior for Medication orders
    // Users can now enter medication orders directly in this modal
  };

  // Handle predefined lab orders modal
  const handlePredefinedLabOrdersModal = () => {
    setIsPredefinedLabOrdersModalVisible(true);
  };

  const handlePredefinedLabOrdersClose = () => {
    setIsPredefinedLabOrdersModalVisible(false);
  };

  const handlePredefinedLabOrdersSelected = async (selectedOrders) => {
    if (!user || !user._id) {
      message.error('User not logged in or user ID missing.');
      return;
    }

    console.log('[AddOrderModal] Adding predefined lab orders:', selectedOrders);
    setLoading(true);

    try {
      // Use bulk order creation API for better performance
      console.log('[AddOrderModal] Creating bulk lab orders with payload:', selectedOrders);
      const result = await axios.post(`/patients/${patientId}/orders/bulk`, {
        orders: selectedOrders
      });
      console.log('[AddOrderModal] Bulk lab orders created successfully:', result.data);

      message.success({
        content: `Successfully added ${selectedOrders.length} laboratory order(s)! Check the Laboratory tab to view them.`,
        duration: 5
      });

      // Close modals and refresh
      setIsPredefinedLabOrdersModalVisible(false);
      if (onOrderCreated) onOrderCreated();
      handleClose();

    } catch (err) {
      console.error("Error adding predefined lab orders:", err);
      const errorMsg = err.response?.data?.message || 'Failed to add laboratory orders.';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to validate only essential fields for draft saves
  const validateDraftFields = async () => {
    try {
      await form.validateFields(['startDate', 'endDate', 'orderType']);
      return form.getFieldsValue(['startDate', 'endDate', 'orderType']);
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (values, saveAsDraft = false) => {
    if (!user || !user._id) {
      message.error('User not logged in or user ID missing.');
      return;
    }

    // Validate date range: Start Date should not be after End Date
    if (values.startDate && values.endDate && moment(values.startDate).isAfter(moment(values.endDate), 'day')) {
      message.error('Start Date cannot be after End Date. Please select valid dates.');
      return;
    }

    setLoading(true);
    try {
      let orderDetailsPayload = {};
      switch(values.orderType) {
        case 'Laboratory': orderDetailsPayload = { testName: values.testName, specimenType: values.specimenType, labName: values.labName }; break;
        case 'Radiology': orderDetailsPayload = { studyDescription: values.studyDescription, reasonForStudy: values.reasonForStudy, imagingCenter: values.imagingCenter }; break;
        case 'Referral': orderDetailsPayload = { referredToProvider: values.referredToProvider, specialty: values.specialty, reasonForReferral: values.reasonForReferral, referralUrgency: values.referralUrgency }; break;
        case 'Medication': orderDetailsPayload = { description: values.description, instructions: values.instructions }; break;
        case 'Procedure': orderDetailsPayload = { description: values.description }; break;
        case 'Nursing': orderDetailsPayload = { description: values.description }; break;
        case 'Consultation': orderDetailsPayload = { description: values.description }; break;
        case 'Preventive Screening': orderDetailsPayload = { description: values.description, instructions: values.instructions }; break;
        case 'Vaccine': orderDetailsPayload = { description: values.description, instructions: values.instructions }; break;
        case 'Lifestyle':
          if (values.lifestyleCategory === 'Nutrition') {
            orderDetailsPayload = {
              lifestyleCategory: 'Nutrition',
              baseDiet: values.baseDiet,
              modifications: values.modifications,
              lifestyleGeneralInstructions: values.lifestyleGeneralInstructions,
            };
          } else {
            orderDetailsPayload = {
              lifestyleCategory: values.lifestyleCategory,
              activityType: values.activityType, frequencyPA: values.frequencyPA, durationPA: values.durationPA, intensityPA: values.intensityPA, specificExercises: values.specificExercises,
              sleepDurationTarget: values.sleepDurationTarget, sleepHygieneRecommendations: values.sleepHygieneRecommendations, stressReductionTechniques: values.stressReductionTechniques,
              psychosocialSupportType: values.psychosocialSupportType, psychosocialReferralDetails: values.psychosocialReferralDetails, psychosocialGoals: values.psychosocialGoals,
              lifestyleGeneralInstructions: values.lifestyleGeneralInstructions,
            };
          }
          Object.keys(orderDetailsPayload).forEach(key => { if (orderDetailsPayload[key] === undefined) delete orderDetailsPayload[key]; });
          break;
        default: orderDetailsPayload = { description: values.description, instructions: values.instructions }; break;
      }

      const payload = {
        orderType: values.orderType,
        frequency: values.frequency,
        startDate: values.startDate ? values.startDate.toISOString() : undefined,
        endDate: values.endDate ? values.endDate.toISOString() : undefined,
        status: saveAsDraft ? 'Pending' : 'Active',
        patient: patientId,
        orderingProvider: user._id,
        orderDetails: orderDetailsPayload,
      };

      await axios.post(`/patients/${patientId}/orders`, payload);
      const successMessage = saveAsDraft
        ? 'Order saved as draft! You can sign it later from your Work Basket.'
        : 'Order added successfully!';
      message.success(successMessage);
      if (onOrderCreated) onOrderCreated();
      handleClose();
    } catch (error) {
      console.error("Error adding order:", error);
      const errorMsg = error.response?.data?.message || 'Failed to add order.';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedOrderType(null);
    onClose();
  };

  const handleLifestyleOrdersSelected = async (selectedOrders) => {
    if (!user || !user._id) {
      message.error('User not logged in or user ID missing.');
      return;
    }

    setLoading(true);
    try {
      const orderPromises = selectedOrders.map(orderData => {
        const payload = {
          orderType: orderData.orderType,
          orderDetails: orderData.orderDetails,
          status: orderData.status || 'Active',
          patient: patientId,
          orderingProvider: user._id,
          priority: orderData.priority || 'Routine',
          startDate: orderData.startDate,
          endDate: orderData.endDate
        };
        return axios.post(`/patients/${patientId}/orders`, payload);
      });

      await Promise.all(orderPromises);
      message.success(`Successfully added ${selectedOrders.length} lifestyle intervention order(s)!`);
      setIsLifestyleOrderCardsModalVisible(false);
      if (onOrderCreated) onOrderCreated();
      handleClose();
    } catch (err) {
      console.error("Error adding lifestyle orders:", err);
      const errorMsg = err.response?.data?.message || 'Failed to add lifestyle orders.';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderConditionalFields = () => {
    switch(selectedOrderType) {
      case 'Laboratory': return (
        <>
          <Divider orientation="left">Laboratory Order Options</Divider>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Button
                type="dashed"
                block
                icon={<ExperimentOutlined />}
                onClick={handlePredefinedLabOrdersModal}
                style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
              >
                <div>Quick Selection</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Choose from predefined lab orders</div>
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="default"
                block
                icon={<PlusOutlined />}
                style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                disabled
              >
                <div>Custom Order</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Enter custom lab details below</div>
              </Button>
            </Col>
          </Row>
          <Divider orientation="left">Or Enter Custom Details</Divider>
          <Form.Item name="testName" label="Test Name" rules={[{ required: true }]}>
            <Input placeholder="Enter custom test name" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="specimenType" label="Specimen Type">
                <Input placeholder="e.g., Blood, Urine" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="labName" label="Lab Name">
                <Input placeholder="Laboratory name" />
              </Form.Item>
            </Col>
          </Row>
        </>
      );
      case 'Radiology': return (<><Form.Item name="studyDescription" label="Study Description" rules={[{ required: true }]}><Input /></Form.Item><Form.Item name="reasonForStudy" label="Reason for Study"><TextArea rows={2} /></Form.Item><Form.Item name="imagingCenter" label="Imaging Center"><Input /></Form.Item></>);
      case 'Referral': return (<><Row gutter={16}><Col span={12}><Form.Item name="referredToProvider" label="Referred To Provider/Service" rules={[{ required: true }]}><Input /></Form.Item></Col><Col span={12}><Form.Item name="specialty" label="Specialty"><Input /></Form.Item></Col></Row><Form.Item name="reasonForReferral" label="Reason for Referral" rules={[{ required: true }]}><TextArea rows={2} /></Form.Item><Form.Item name="referralUrgency" label="Urgency" initialValue="Routine"><Select><Option value="Routine">Routine</Option><Option value="Urgent">Urgent</Option><Option value="STAT">STAT</Option></Select></Form.Item></>);
      case 'Procedure': return <Form.Item name="description" label="Procedure Description" rules={[{ required: true }]}><TextArea rows={2} /></Form.Item>;
      case 'Nursing': return <Form.Item name="description" label="Nursing Order Details" rules={[{ required: true }]}><TextArea rows={2} /></Form.Item>;
      case 'Consultation': return <Form.Item name="description" label="Consultation Request" rules={[{ required: true }]}><TextArea rows={2} /></Form.Item>;
      case 'Medication': return (<><Form.Item name="description" label="Medication Description" rules={[{ required: true }]}><TextArea rows={2} placeholder="Enter medication details, dosage, frequency, etc." /></Form.Item><Form.Item name="instructions" label="Instructions"><TextArea rows={2} placeholder="Additional instructions for medication administration" /></Form.Item></>);
      case 'Lifestyle':
        return (
          <><Form.Item name="lifestyleCategory" label="Lifestyle Category" rules={[{ required: true }]}><Select placeholder="Select lifestyle category"><Option value="Nutrition">Nutrition</Option><Option value="PhysicalActivity">Physical Activity</Option><Option value="Rest">Rest</Option><Option value="PsychosocialManagement">Psychosocial Management</Option></Select></Form.Item>
            {form.getFieldValue('lifestyleCategory') === 'Nutrition' && <NutritionOrderForm form={form} />}
            {form.getFieldValue('lifestyleCategory') === 'PhysicalActivity' && (<><Form.Item name="activityType" label="Activity Type (e.g., Aerobic, Strength)"><Input /></Form.Item><Form.Item name="frequencyPA" label="Frequency (e.g., 5 times/week)"><Input /></Form.Item><Form.Item name="durationPA" label="Duration (e.g., 30 mins/session)"><Input /></Form.Item><Form.Item name="intensityPA" label="Intensity (e.g., Moderate)"><Input /></Form.Item><Form.Item name="specificExercises" label="Specific Exercises"><TextArea rows={2} /></Form.Item></>)}
            {form.getFieldValue('lifestyleCategory') === 'Rest' && (<><Form.Item name="sleepDurationTarget" label="Sleep Duration Target (e.g., 7-8 hours)"><Input /></Form.Item><Form.Item name="sleepHygieneRecommendations" label="Sleep Hygiene Recommendations"><TextArea rows={2} /></Form.Item><Form.Item name="stressReductionTechniques" label="Stress Reduction Techniques"><TextArea rows={2} /></Form.Item></>)}
            {form.getFieldValue('lifestyleCategory') === 'PsychosocialManagement' && (<><Form.Item name="psychosocialSupportType" label="Support Type (e.g., Counseling, Group)"><Input /></Form.Item><Form.Item name="psychosocialReferralDetails" label="Referral Details (Contact, etc.)"><TextArea rows={2} /></Form.Item><Form.Item name="psychosocialGoals" label="Psychosocial Goals"><TextArea rows={2} /></Form.Item></>)}
            <Form.Item name="lifestyleGeneralInstructions" label="General Lifestyle Instructions"><TextArea rows={2} /></Form.Item></>
        );
      case 'Preventive Screening': return (<><Form.Item name="description" label="Screening Description" rules={[{ required: true, message: 'Please enter screening description!' }]}><TextArea rows={2} placeholder="Describe the preventive screening or test..." /></Form.Item><Form.Item name="instructions" label="Instructions"><TextArea rows={2} placeholder="Pre-screening preparation or follow-up instructions..." /></Form.Item></>);
      case 'Vaccine': return (<><Form.Item name="description" label="Vaccine Description" rules={[{ required: true, message: 'Please enter vaccine description!' }]}><Input placeholder="Vaccine name, type, and dosage..." /></Form.Item><Form.Item name="instructions" label="Instructions"><TextArea rows={2} placeholder="Administration instructions, precautions, follow-up..." /></Form.Item></>);
      default: return (<><Form.Item name="description" label="Description" rules={[{ required: selectedOrderType === 'Other' || selectedOrderType === 'Procedure' || selectedOrderType === 'Nursing' || selectedOrderType === 'Medication' || selectedOrderType === 'Consultation'}]}><Input /></Form.Item><Form.Item name="instructions" label="Instructions"><TextArea rows={2} /></Form.Item></>);
    }
  };

  return (
    <>
      <Modal
        title={title}
        open={visible}
        onCancel={handleClose}
        footer={null}
        destroyOnClose
        width={700}
        forceRender
      >
        <Spin spinning={loading}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="startDate"
                  label={<span>Start Date <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: 'Start date is required!' }]}
                >
                  <MomentDatePicker
                    style={{ width: '100%' }}
                    placeholder="Select start date"
                    disabledDate={(current) => {
                      const endDate = form.getFieldValue('endDate');
                      return endDate && current && current > moment(endDate).startOf('day');
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="endDate"
                  label={<span>End Date <span style={{ color: 'red' }}>*</span></span>}
                  rules={[{ required: true, message: 'End date is required!' }]}
                >
                  <MomentDatePicker
                    style={{ width: '100%' }}
                    placeholder="Select end date"
                    disabledDate={(current) => {
                      const startDate = form.getFieldValue('startDate');
                      return startDate && current && current < moment(startDate).startOf('day');
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="frequency"
              label="Order Frequency"
              rules={[{ required: true, message: 'Please select order frequency!' }]}
            >
              <Select placeholder="Select frequency" style={{ width: '100%' }}>
                <Option value="One-time">One-time order</Option>
                <Option value="Continuous">Continuous order (standing order)</Option>
                <Select.OptGroup label="Standard Medical Frequencies">
                  <Option value="BID">BID (twice daily)</Option>
                  <Option value="TID">TID (three times daily)</Option>
                  <Option value="QID">QID (four times daily)</Option>
                  <Option value="QD">QD (once daily)</Option>
                  <Option value="QOD">QOD (every other day)</Option>
                  <Option value="PRN">PRN (as needed)</Option>
                </Select.OptGroup>
                <Select.OptGroup label="Time-based Intervals">
                  <Option value="Daily">Daily</Option>
                  <Option value="Every other day">Every other day</Option>
                  <Option value="Weekly">Weekly</Option>
                  <Option value="Bi-weekly">Bi-weekly</Option>
                  <Option value="Monthly">Monthly</Option>
                  <Option value="Every 2 months">Every 2 months</Option>
                  <Option value="Every 3 months">Every 3 months</Option>
                  <Option value="Every 6 months">Every 6 months</Option>
                  <Option value="Annually">Annually</Option>
                </Select.OptGroup>
              </Select>
            </Form.Item>
            <Form.Item name="orderType" label="Order Type" rules={[{ required: true, message: 'Please select order type!' }]}>
              <Radio.Group onChange={(e) => handleOrderTypeChange(e.target.value)} buttonStyle="solid">
                <Radio.Button value="Nursing">Nursing</Radio.Button>
                <Radio.Button value="Medication">Medication</Radio.Button>
                <Radio.Button value="Laboratory">Laboratory</Radio.Button>
                <Radio.Button value="Radiology">Radiology</Radio.Button>
                <Radio.Button value="Referral">Referral</Radio.Button>
                <Radio.Button value="Lifestyle">Lifestyle</Radio.Button>
                <Radio.Button value="Procedure">Procedure</Radio.Button>
                <Radio.Button value="Preventive Screening">Screening</Radio.Button>
                <Radio.Button value="Vaccine">Vaccine</Radio.Button>
                <Radio.Button value="Other">Other</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.orderType !== currentValues.orderType || prevValues.lifestyleCategory !== currentValues.lifestyleCategory}>
              {() => selectedOrderType && renderConditionalFields()}
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} disabled={!selectedOrderType}>
                Add & Sign Order
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={async () => {
                  try {
                    const essentialFields = await validateDraftFields();
                    const allValues = form.getFieldsValue();
                    handleSubmit(allValues, true);
                  } catch (error) {
                    // Validation errors will be shown automatically by form
                  }
                }}
                loading={loading}
                disabled={!selectedOrderType}
              >
                Save as Draft
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleClose}>Cancel</Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
      <LifestyleOrderCardsModal
        visible={isLifestyleOrderCardsModalVisible}
        onClose={() => setIsLifestyleOrderCardsModalVisible(false)}
        onOrdersSelected={handleLifestyleOrdersSelected}
        patientId={patientId}
      />
      <PredefinedLabOrdersModal
        visible={isPredefinedLabOrdersModalVisible}
        onClose={handlePredefinedLabOrdersClose}
        onOrdersSelected={handlePredefinedLabOrdersSelected}
        patientId={patientId}
        title="Select Predefined Laboratory Orders"
      />
    </>
  );
};

export default AddOrderModal;
